import React, { useState, useRef, useEffect } from 'react';
import './PinInput.css';

interface PinInputProps {
  length: number;
  onComplete: (pin: string) => void;
  error?: boolean;
  disabled?: boolean;
}

export const PinInput: React.FC<PinInputProps> = ({ 
  length, 
  onComplete, 
  error = false,
  disabled = false 
}) => {
  const [values, setValues] = useState<string[]>(new Array(length).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (error) {
      setValues(new Array(length).fill(''));
      inputRefs.current[0]?.focus();
    }
  }, [error, length]);

  const handleChange = (index: number, value: string) => {
    if (disabled) return;
    
    if (value.length > 1) {
      value = value.slice(-1);
    }

    if (!/^\d*$/.test(value)) {
      return;
    }

    const newValues = [...values];
    newValues[index] = value;
    setValues(newValues);

    if (value && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    if (newValues.every(v => v !== '') && newValues.join('').length === length) {
      onComplete(newValues.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    if (e.key === 'Backspace' && !values[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    if (disabled) return;
    
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').slice(0, length);
    
    if (!/^\d+$/.test(pastedData)) {
      return;
    }

    const newValues = [...values];
    pastedData.split('').forEach((char, i) => {
      if (i < length) {
        newValues[i] = char;
      }
    });
    setValues(newValues);

    if (pastedData.length >= length) {
      onComplete(newValues.join(''));
    } else {
      inputRefs.current[pastedData.length]?.focus();
    }
  };

  return (
    <div className={`pin-input-container ${error ? 'error' : ''}`}>
      {values.map((value, index) => (
        <input
          key={index}
          ref={el => {
            inputRefs.current[index] = el;
          }}
          type="password"
          inputMode="numeric"
          pattern="\d*"
          className="pin-input"
          value={value}
          onChange={e => handleChange(index, e.target.value)}
          onKeyDown={e => handleKeyDown(index, e)}
          onPaste={handlePaste}
          disabled={disabled}
          maxLength={1}
          autoComplete="off"
        />
      ))}
    </div>
  );
};